import React, { useState, useEffect } from 'react';
import { Grid, Button, FormControl, FormLabel, FormControlLabel, FormGroup, Checkbox, MenuItem, Select } from '@mui/material';
import FilterIcon from '@mui/icons-material/FilterList';
import { useSelector, useDispatch } from 'react-redux';
import { get, size, set } from 'global/lib/lodash';
import setFilter from 'redux/actions/setFilter';
import { EditionsFilter, FILTERDEFAULTS } from 'global/utils/filter';
import { useScreen } from 'global/styles';
import { FilterRow, FlexFilters, filterFormGridSx, filterFormItemSx, filterFormLabelSx, filterIconSx, filterHiddenSx } from 'global/styles/filters';
import { EditionStatus } from 'global/lib/filter';
import DiagContainer from 'global/components/diag-container';
import { FilterChips } from 'global/components/filterchips';
import { Row } from 'global/components/row';
import { CourseStatus } from 'global/lib/filter';
import { DatePicker } from 'global/components/date-picker'
import useRetrieveData from 'global/hooks/useRetrieveData';
import { stringIt, cleanCopy } from 'global/utils';
import { useUser } from '../../../global/hooks/useUser';

export default function PerformEditionsFilters(props) {
  const { display, courses } = props;
  const [showFilters, setShowFilters] = useState(false);
  const filter = useSelector((state) => get(state, `filter.library.${display}`, EditionsFilter()));
  const dispatch = useDispatch();
  const { status, courseStatus, edition_id, start_date, end_date, user_groups } = filter;
  const { me } = useUser();

  const { isMobile } = useScreen();

  const editions = useRetrieveData(
    { url: `/editions` },
    {
      build: ['editions'],
      params: {
        filter: {
          edition_type: 'course',
        },
      },
    }
  );

  const usergroups = useRetrieveData(
    { url: `/user-groups` },
    {
      build: ['userGroups'],
    }
  );
  window.EDITIONSDATA = editions.data;

  useEffect(() => {
    if (!me) {
      return;
    }
    changeFilter({ ...filter, user_groups: get(me, 'userGroups', []).map(ug => ug.id) })
  }, [me?.userGroups]);

  useEffect(() => {
    setShowFilters(!isMobile);
  }, [isMobile]);

  useEffect(() => {
    if (display == 'editions' && (get(filter, 'role') == 'reviewer' || get(filter, 'searchstring'))) {
      dispatch(setFilter('library', display, { ...filter, role: null, searchstring: '' }));
    }
  }, [display]);

  const changeFilter = (filt) => {
    dispatch(setFilter('library', display, filt));
  };

  const handleStatusChange = (name) => (e) => {
    const exists = status.indexOf(name);
    const filt = { ...filter };
    if (exists !== -1) {
      filt.status.splice(exists, 1);
    } else {
      filt.status.push(name);
    }
    changeFilter(filt);
  };

  const handleGroupChange = (name) => (e) => {
    const exists = user_groups.indexOf(name);
    const filt = { ...filter };
    if (exists !== -1) {
      filt.user_groups.splice(exists, 1);
    } else {
      filt.user_groups.push(name);
    }
    changeFilter(filt);
  };

  const handleCourseStatusChange = (name) => (e) => {
    const exists = courseStatus.map(stringIt).indexOf(stringIt(name));
    const filt = { ...filter };
    if (exists !== -1) {
      filt.courseStatus.splice(exists, 1);
    } else {
      filt.courseStatus.push(name);
    }
    changeFilter(filt);
  };

  function handleCourseChange(e) {
    changeFilter(set({ ...filter }, `edition_id`, e.target.value));
  }

  function handleStartDateChange(date) {
    changeFilter(set({ ...filter }, `start_date`, date));
  }

  function handleEndDateChange(date) {
    changeFilter(set({ ...filter }, `end_date`, date));
  }

  function toggleFilters() {
    setShowFilters(!showFilters);
  }

  function clearFilters() {
    changeFilter(cleanCopy(get(FILTERDEFAULTS, `library.${display}`, EditionsFilter())));
  }

  const showCourses = get(props, 'showCourses', true) && !courses.loading && !!size(courses.data);

  const extraFilters = display === 'courses';

  const allDocuments = editions.data;

  return (
    <div>
      {isMobile && (
        <FilterRow isMobile>
          <Button aria-label="filters" size="small" onClick={toggleFilters}>
            <FilterIcon fontSize="inherit" sx={filterIconSx} /> {showFilters ? 'Hide Filters' : 'Filters'}
          </Button>
          <FilterChips dontShow={display == 'courses' ? ['user_groups'] : undefined} id={`library.${display}`} />
        </FilterRow>
      )}

      <DiagContainer useDialog={isMobile} title="Filters" onClose={toggleFilters} onClear={clearFilters} open={showFilters}>
        <form autoComplete="off" margin="normal" style={!showFilters ? filterHiddenSx : undefined}>
          <Grid container>
            {showCourses && (
              <Grid item md={12} sx={filterFormGridSx}>
                <FlexFilters>
                  <FormControl component="fieldset" sx={filterFormItemSx}>
                    <FormLabel component="legend" sx={filterFormLabelSx}>
                      Course Status
                    </FormLabel>
                    <FormGroup>
                      {CourseStatus.map((ed, i) => {
                        const checked = courseStatus.map(stringIt).indexOf(stringIt(ed.value)) > -1;
                        return (
                          <FormControlLabel
                            key={i}
                            control={<Checkbox checked={!!checked} onChange={handleCourseStatusChange(ed.value)} value={ed.value} />}
                            label={
                              <Row>
                                {ed.icon}
                                {ed.title}
                              </Row>
                            }
                          />
                        );
                      })}
                    </FormGroup>
                  </FormControl>
                </FlexFilters>
              </Grid>
            )}

            {extraFilters && (
              <>
                <Grid item xs={12} sx={filterFormGridSx}>
                  <FormControl variant="standard" sx={filterFormItemSx}>
                    <FormLabel htmlFor="Standard-scorecard-simple" sx={filterFormLabelSx}>
                      Course
                    </FormLabel>
                    <Select value={edition_id || 0} onChange={handleCourseChange}>
                      <MenuItem value={0}>
                        <em>Any</em>
                      </MenuItem>
                      {!!size(allDocuments) &&
                        (allDocuments || []).map((card, i) => {
                          return (
                            <MenuItem key={`${card.id}-${i}`} value={card.id}>
                              {get(card, 'name', 'none')}
                            </MenuItem>
                          );
                        })}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sx={filterFormGridSx}>
                  <DatePicker
                    id="mui-pickers-date_start"
                    label="Start Date"
                    autoOk={true}
                    value={start_date}
                    onChange={handleStartDateChange}
                    sx={filterFormItemSx}
                    KeyboardButtonProps={{
                      'aria-label': 'start date',
                    }}
                  />
                </Grid>
                <Grid item xs={12} sx={filterFormGridSx}>
                  <DatePicker
                    id="mui-pickers-date_end"
                    label="End Date"
                    autoOk={true}
                    value={end_date}
                    onChange={handleEndDateChange}
                    sx={filterFormItemSx}
                    KeyboardButtonProps={{
                      'aria-label': 'end date',
                    }}
                  />
                </Grid>
              </>
            )}

            {!extraFilters && (
              <Grid item md={12} sx={filterFormGridSx}>
                <FlexFilters>
                  <FormControl component="fieldset" sx={filterFormItemSx}>
                    <FormLabel component="legend" sx={filterFormLabelSx}>
                      Edition Status
                    </FormLabel>
                    <FormGroup sx={{ mt: 1 }}>
                      {EditionStatus.map((ed, i) => (
                        <FormControlLabel key={i} control={<Checkbox checked={status.indexOf(ed.value) > -1} onChange={handleStatusChange(ed.value)} value={ed.value} />} label={ed.title} />
                      ))}
                    </FormGroup>
                  </FormControl>
                </FlexFilters>
              </Grid>
            )}

            {!extraFilters && (
              <Grid item md={12} sx={filterFormGridSx}>
                <FlexFilters>
                  <FormControl component="fieldset" sx={filterFormItemSx}>
                    <FormLabel component="legend" sx={filterFormLabelSx}>
                      User Groups
                    </FormLabel>
                    <FormGroup sx={{ mt: 1 }}>
                      {usergroups.data.map((ed, i) => (
                        <FormControlLabel key={i} control={<Checkbox checked={(user_groups || []).indexOf(ed.id) > -1} onChange={handleGroupChange(ed.id)} value={ed.id} />} label={ed.name} />
                      ))}
                    </FormGroup>
                  </FormControl>
                </FlexFilters>
              </Grid>
            )}
          </Grid>
        </form>
      </DiagContainer>
    </div>
  );
}
